import React, { useState, useEffect, useReducer, useRef, useMemo } from "react";
import {
  Grid,
  Box,
  Card,
  IconButton,
  Tooltip,
  Input,
  InputAdornment,
  Typography,
  Stack,
} from "@mui/material";
import dayjs from "dayjs";
import { get } from "aws-amplify/api";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiRef,
} from "@mui/x-data-grid";
import { I18n } from "@aws-amplify/core";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import currentUserToken from "CurrentUserToken";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import { post } from "aws-amplify/api";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import { APIStatusDialog } from "popup/APIStatusDialog";
import useMediaQuery from "@mui/material/useMediaQuery";
import SplitterLayout from "react-splitter-layout";
import StoreDetail from "layouts/dashboard/components/StoreDetail";
import { DataGridPro } from "@mui/x-data-grid-pro/DataGridPro";
import { itIT } from "@mui/x-data-grid/locales";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import { deDE } from "@mui/x-data-grid/locales";
import { FiberManualRecord } from "@mui/icons-material";
const COLUMN_VISIBILITY_KEY = "columnVisibility_stores";
const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      audit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).format(
            "YYYY-MM-DD HH:mm"
          );
        }
      });
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};
const loadFromLocalStorage = (key, defaultValue) => {
  const saved = localStorage.getItem(key);
  return saved ? JSON.parse(saved) : defaultValue;
};
function Store() {
  const [state, dispatch] = useReducer(reducer, { audit: [], fetched: false });
  const theme = useTheme();
  const mobileDevice = !useMediaQuery(theme.breakpoints.up("md"));
  const [selectedRowData, setSelectedRowData] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [resizeThrottle, setResizeThrottle] = useState(60);
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [languages, setLanguages] = useState([]);
  const [profile, setProfile] = useState([]);
  const [language, setLanguage] = useState(
    localStorage.getItem("language") || "en"
  );
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [tableParentIsMD, setTableParentIsMD] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const toolbarRef = useRef(null);
  const [pinnedColumns, setPinnedColumns] = useState(() =>
    loadFromLocalStorage("storePinnedColumns", { left: [], right: ["action"] })
  );

  useEffect(() => {
    localStorage.setItem("storePinnedColumns", JSON.stringify(pinnedColumns));
  }, [pinnedColumns]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath.includes("/settings")) {
      setTabValue(0);
    } else if (currentPath.includes("/uncoded")) {
      setTabValue(1);
    }
  }, []);
  useEffect(() => {
    const savedColumnVisibility = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (savedColumnVisibility) {
      setColumnVisibilityModel(JSON.parse(savedColumnVisibility));
    } else {
      setColumnVisibilityModel({
        id: false,
        source_technology: false,
        datetime_close: false,
        alarm_text: false,
        asset: false,
        serial_number: false,
        billing_city: true,
        billing_street: false,
        retailer: false,
        phone: false,
        email: true,
        billing_country: true,
        account_number: false,
        fax: false,
        secondary_note: false,
        primary_note: false,
        website: false,
        tls_contract_type: false,
        tls_contract_end_date: false,
        tls_contract_start_date: false,
        tls_activation_date: false,
        billing_postal_code: false,
        tlm_active: false,
        billing_country_code: false,
        store_management: true,
        store_name: false,
      });
    }
  }, []);

  useEffect(() => {
    const parentElement = toolbarRef.current;
    if (!parentElement) {
      console.error("toolbarRef is not correctly attached to any DOM element.");
      return;
    }
    const checkSize = (width) => {
      const breakpoint = 960; // 960px is md
      setTableParentIsMD(width < breakpoint);
    };
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        checkSize(entry.contentRect.width);
      }
    });
    resizeObserver.observe(parentElement);
    checkSize(parentElement.offsetWidth);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const [searchText, setSearchText] = useState("");
  const [rowModesModel, setRowModesModel] = useState({});
  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };

  const filteredRows = state.audit.filter((value) => {
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      return Object.values(value).some((val) =>
        String(val).toLowerCase().includes(searchTextLower)
      );
    }
    return true;
  });

  const filteredRowsCount = filteredRows.length;
  const tlmGetAccoutsCountryCode = async () => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/accountNumber/get",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();

      dispatch({ type: "DATA_RECEIVED", value: response });
    } catch (error) {
      console.error("Errore durante la chiamata API:", error);
    }
  };
  useEffect(() => {
    tlmGetAccoutsCountryCode();
  }, []);

  useEffect(() => {
    const tlmGetLanguagesAPI = async () => {
      try {
        const restOperation = get({
          apiName: "TelemonitoringAPI",
          path: "/languages",
          options: {
            headers: {
              "Content-Type": "application/json",
              Authorization: `${await currentUserToken()}`,
            },
            queryParams: {},
          },
        });
        const { body } = await restOperation.response;
        const response = await body.json();

        setLanguages(response);
      } catch (error) {
        console.error("Errore durante la chiamata API:", error);
      }
    };
    tlmGetLanguagesAPI();
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "account_number",
        headerName: I18n.get("Account Number"),
        width: 200,
      },
      {
        field: "name",
        headerName: I18n.get("Account Name"),
        width: 400,
      },
      {
        field: "store_name",
        headerName: I18n.get("Store Name"),
        minWidth: 450,
      },
      {
        field: "tlm_active",
        headerName: I18n.get("TLM Active"),
        width: 200,
      },
      {
        field: "inactive_store",
        headerName: I18n.get("Suspended Store"),
        width: 200,
      },

      {
        field: "store_management",
        headerName: I18n.get("Store Management"),
        width: 200,
      },
      {
        field: "offline",
        headerName: I18n.get("Offline"),
        width: 150,
        // align:"center", //centra il contenuto della cella
        renderCell: (params) => (
          <FiberManualRecord
            sx={{
              color: params.value ? "red" : "green",
              height: "1.5rem",
              width: "1.5rem",
              margin: "0.35rem",
            }}
          />
        ),
      },
      {
        field: "shift",
        headerName: I18n.get("Shift"),
        width: 200,
      },
      {
        field: "billing_country",
        headerName: I18n.get("Country"),
        width: 200,
      },

      {
        field: "billing_city",
        headerName: I18n.get("City"),
        width: 200,
      },
      {
        field: "phone",
        headerName: I18n.get("Phone"),
        width: 200,
        renderCell: (params) => (
          <Typography
            component="a"
            href={`tel:${params.value}`}
            sx={{
              fontSize: "medium",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      { field: "fax", headerName: I18n.get("Fax"), width: 200 },
      {
        field: "email",
        headerName: I18n.get("Email"),
        width: 350,
        renderCell: (params) => (
          <Typography
            component="a"
            href={`mailto:${params.value}`}
            sx={{
              fontSize: "medium",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {params.value}
          </Typography>
        ),
      },

      {
        field: "billing_country_code",
        headerName: I18n.get("Country Code"),
        width: 200,
      },
      {
        field: "billing_postal_code",
        headerName: I18n.get("Postal Code"),
        width: 200,
      },
      {
        field: "retailer",
        headerName: I18n.get("Retailer Code"),
        width: 200,
      },

      {
        field: "retailer_description",
        headerName: I18n.get("Retailer"),
        width: 400,
      },

      {
        field: "tls_activation_date",
        headerName: I18n.get("Activation Date"),
        width: 200,
        valueFormatter: (value) => {
          if (!value) {
            return ""; // Handle null or undefined values
          }
          const formattedDate = dayjs
            .utc(value)
            .tz(dayjs.tz.guess())
            .format("YYYY-MM-DD HH:mm:ss");
          return formattedDate;
        },
      },
      {
        field: "tls_contract_start_date",
        headerName: I18n.get("Contract Start Date"),
        width: 200,
      },
      {
        field: "tls_contract_end_date",
        headerName: I18n.get("Contract End Date"),
        width: 200,
      },
      {
        field: "tls_contract_type",
        headerName: I18n.get("Contract Type"),
        width: 200,
      },
      {
        field: "website",
        headerName: I18n.get("Website"),
        width: 200,
      },
      // {
      //   field: "primary_note",
      //   headerName: I18n.get("Plant Important Note"),
      //   width: 200,
      // },
      {
        field: "secondary_note",
        headerName: I18n.get("Contact Note"),
        width: 200,
      },
      // {
      //   field: "service_territory_name",
      //   headerName: I18n.get("Service Territory"),
      //   width: 200,
      // },
      {
        field: "action",
        headerName: I18n.get("Action"),
        resizable: false,
        width: 100,
        renderCell: (params) => (
          <Tooltip title={I18n.get("Open in Salesforce")}>
            <IconButton
              component="a"
              href={`https://eptarefrigeration.lightning.force.com/${params.row.id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                width: 30,
                height: 30,
                color: "inherit", // Change icon color
              }}
              onClick={(event) => event.stopPropagation()} // Prevent row selection on click
            >
              <InsertLinkIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    []
  );

  const handlePopUpButtonClick = (selectedRow) => {
    setSelectedRowData([selectedRow]); // Ensure the selected row data is updated
    setShowPopup(true); // Show the popup when a row is clicked
  };

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(newModel));
  };

  const renderToolbar = () => {
    return (
      <div ref={toolbarRef}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" mt={0.5} mb={1}>
            {I18n.get("Stores")} ({filters ? filteredRowsCount : totalCount})
          </Typography>
        </Stack>
        <Stack
          direction={tableParentIsMD ? "column" : "row"}
          alignItems="center"
          justifyContent={tableParentIsMD ? "center" : "space-between"}
          sx={{ pb: 1 }}
        >
          <Stack
            direction={tableParentIsMD ? "column" : "row"}
            spacing={2}
            sx={{ width: "100%" }}
          ></Stack>
          <Input
            type="text"
            placeholder={I18n.get("Search")}
            onChange={searchChanged}
            fullWidth={tableParentIsMD}
            endAdornment={
              <InputAdornment position="end">
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </Stack>
      </div>
    );
  };
  function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Firefox")) {
      return "Firefox";
    } else if (userAgent.includes("Edg")) {
      return "Edge";
    }
  }
  const browser = getBrowserName();
  useEffect(() => {
    if (browser === "Firefox") {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "auto";
      };
    }
  }, []);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 500,
  });
  const [tableHeight, setTableHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      const navbarHeight = document.getElementById("navbar");
      const footerHeight = document.getElementById("footer");
      const availableHeight =
        document.documentElement.clientHeight -
        navbarHeight -
        footerHeight -
        90;
      setTableHeight(availableHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  const darkMode = localStorage.getItem("darkMode") === "true";
  return (
    <>
      <DashboardLayout>
        <DashboardNavbar
          absolute
          light
          language={language}
          setLanguage={setLanguage}
          profile={profile} // Pass profile
          setProfile={setProfile}
        />
        <SplitterLayout
          vertical={mobileDevice}
          percentage={true}
          primaryIndex={0}
          primaryMinSize={mobileDevice ? 30 : 50}
          secondaryMinSize={35}
          secondaryInitialSize={mobileDevice ? 30 : 50}
          customClassName="splitter-layout-custom"
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ p: 2, maxHeight: `${tableHeight}px` }}>
                {" "}
                {/** lasciare così */}
                <Card
                  sx={{
                    p: 3,
                    maxHeight: `${tableHeight}px`,
                  }}
                >
                  {renderToolbar()}

                  <DataGridPro
                    sx={{
                      "& .MuiDataGrid-root, & .MuiDataGrid-footerContainer": {
                        minHeight: "2rem",
                      },
                      "&  .MuiDataGrid-cell ": {
                        cursor: "default",
                      },
                      borderColor: darkMode ? "#6B635A" : "",
                      "& .MuiDataGrid-cell, &.MuiDataGrid-columnHeaders": {
                        borderColor: darkMode ? "#625a52" : "",
                      },
                      "& .MuiDataGrid-columnSeparator": {
                        borderColor: darkMode ? "#625a52" : "",
                      },
                      "--DataGrid-rowBorderColor": darkMode ? "#625a52" : "",
                      minHeight: "20vh",
                      flexFlow: 1,
                      height: "100%",
                    }}
                    localeText={
                      language === "it"
                        ? itIT.components.MuiDataGrid.defaultProps.localeText
                        : language === "ge"
                        ? deDE.components.MuiDataGrid.defaultProps.localeText
                        : ""
                    }
                    loading={!state.fetched || resizeThrottle > 60}
                    initialState={{
                      density: "compact",
                      sorting: {
                        sortModel: [{ field: "name", sort: "asc" }],
                      },
                      pinnedColumns: pinnedColumns, // Carica le colonne fissate dal localStorage
                    }}
                    onPinnedColumnsChange={(newPinnedColumns) => {
                      setPinnedColumns(newPinnedColumns); // Aggiorna lo stato
                    }}
                    resizeThrottleMs={resizeThrottle}
                    rows={filteredRows}
                    columns={columns}
                    getRowId={(row) => row.id}
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={(newModel) =>
                      setRowModesModel(newModel)
                    }
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    hideFooter
                    // pagination
                    // paginationModel={paginationModel} // Usa uno stato per la paginazione
                    // onPaginationModelChange={(model) => setPaginationModel(model)}
                    // pageSizeOptions={[10, 25, 50, 100, 300, 500]}
                    onRowClick={(params) => handlePopUpButtonClick(params.row)}
                    slotProps={{
                      loadingOverlay: {
                        variant: "skeleton",
                        noRowsVariant: "skeleton",
                      },
                    }}
                    slots={{
                      toolbar: () => (
                        <GridToolbarContainer>
                          <GridToolbarColumnsButton
                            title={I18n.get("Columns")}
                          />
                          <GridToolbarExport
                            printOptions={{ disableToolbarButton: true }}
                            csvOptions={{
                              fileName: I18n.get("Store List"),
                              utf8WithBom: true,
                            }}
                            title={I18n.get("Export")}
                          />

                          <GridToolbarFilterButton
                            title={I18n.get("Filters")}
                          />
                        </GridToolbarContainer>
                      ),
                    }}
                  />
                </Card>
              </Box>
            </Grid>
          </Grid>
          {showPopup && (
            <StoreDetail
              isOpen={showPopup}
              onClose={() => {
                setShowPopup(false);
              }}
              onCloseNote={() => {
                tlmGetAccoutsCountryCode();
              }}
              profile={profile}
              rowData={selectedRowData}
              language={language}
              tlmGetAccoutsCountryCode={tlmGetAccoutsCountryCode}
            />
          )}
        </SplitterLayout>
      </DashboardLayout>
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />
    </>
  );
}
export default Store;
